import { Injectable } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { CollectionPopupComponent } from 'src/app/components/collections/collection-popup/collection-popup.component';
import { ApiService } from 'src/app/services/api.service';
import { DownloadService } from 'src/app/services/download.service';
import { PopupService } from 'src/app/services/popup.service';

@Injectable({
  providedIn: 'root',
})
export class IndicesDownloadService {
  private freeze = false;

  constructor(
    private apiService: ApiService,
    private popupService: PopupService,
    private spinnerService: NgxSpinnerService,
    private downloadService: DownloadService
  ) {}

  downloadIndices(
    indices: any,
    collectionIndex: number,
    sensor: any,
    sensorIndex: number,
    taskId: string
  ) {
    const currentScene = sensor.scenes.find((scene) => scene.checked);

    const isDownloaded = indices.products?.some(
      (el) => el.sceneId === currentScene.id && el.isDownloaded
    );

    if (this.freeze) {
      this.popupService.openPopup(
        CollectionPopupComponent,
        'A download is in progress. Please wait until the current download finishes to continue downloading others.',
        '',
        '40%'
      );
      return;
    }

    if (isDownloaded) {
      this.popupService.openPopup(
        CollectionPopupComponent,
        'download_vadp',
        '',
        '40%'
      );
      return;
    }

    this.spinnerService.show();
    this.freeze = true;

    const sensorObj = {
      taskId: taskId,
      sensorId: indices.sensorId,
      sceneId: currentScene.id,
      product: indices.product,
      type: 'download',
    };

    this.apiService.getIndicesDataImgUrl(sensorObj).subscribe((res: any) => {
      const urls = res?.response?.signedUrls || [];
      if (!urls.length) {
        this.spinnerService.hide();
        this.freeze = false;
        return;
      }

      this.downloadUrlsSequentially(
        urls,
        taskId,
        sensor,
        currentScene,
        indices.product
      )
        .then(() => {
          this.spinnerService.hide();
          this.freeze = false;
        })
        .catch(() => {
          this.spinnerService.hide();
          this.freeze = false;
          this.popupService.openPopup(
            CollectionPopupComponent,
            'Failed to download one or more files.',
            '',
            '40%'
          );
        });
    });
  }

  private async downloadUrlsSequentially(
    urls: any[],
    taskId: string,
    sensor: any,
    currentScene: any,
    product: string
  ) {
    for (const item of urls) {
      const url = item.imageUrl;
      if (!url) continue;

      await this.downloadFile(url, taskId, sensor, currentScene, product);
    }
  }

  private downloadFile(
    url: string,
    taskId: string,
    sensor: any,
    currentScene: any,
    product: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url);
      xhr.responseType = 'blob';

      xhr.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = ((event.loaded / event.total) * 100).toFixed(
            2
          );
          this.downloadService.setProgress({
            progress: percentComplete,
            taskId,
            sensorId: sensor.id,
            sceneId: currentScene.id,
            download: true,
            product,
            action: 'indicesDownload',
          });
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const blob = xhr.response;
          const fileName = this.getFileNameFromS3SignedUrl(url);
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(link.href);
          resolve();
        } else {
          reject();
        }
      };

      xhr.onerror = () => reject();
      xhr.send();
    });
  }

  private getFileNameFromS3SignedUrl(url: string): string {
    const parts = url.split('?')[0].split('/');
    return parts[parts.length - 1];
  }
}
